import i18n from "@/i18n";
import { apiFetcher } from "@/services/API/API.service";
import { AgentModel, agentModelSchema } from "@/types";
import { validateArrayWithSchema } from "@/utils/dataValidators.zod";

// #region fetchAgentModels
export async function fetchAgentModels({
  signal,
  retry = false,
}: {
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      rData: AgentModel[];
    }
  | {
      rStatus: "error";
      rMessage: string;
    }
> {
  if (retry) {
    console.log("Retrying fetchAgents");
  }

  try {
    const response = await apiFetcher({
      route: "/agent/list",
      method: "POST",
      signal: signal,
    });

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<AgentModel>(
        response.data,
        agentModelSchema,
        "Agent"
      );

      if (!validatedData) {
        console.error("Invalid agents data", response.data);
        return {
          rStatus: "error",
          rMessage: i18n.t("Invalid agents data"),
        };
      }

      return {
        rStatus: "success",
        rData: validatedData,
      };
    }

    if (response.status === 204) {
      console.error("No agents found");
      return {
        rStatus: "error",
        rMessage: i18n.t("No agents found"),
      };
    }

    if (response.status === 400) {
      console.error("Bad request");
      return {
        rStatus: "error",
        rMessage: i18n.t("Bad request"),
      };
    }

    if (response.status === 401) {
      console.error("Unauthorized");
      return {
        rStatus: "error",
        rMessage: i18n.t("Unauthorized"),
      };
    }

    if (response.status === 403) {
      console.error("Forbidden");
      return {
        rStatus: "error",
        rMessage: i18n.t("Forbidden"),
      };
    }

    if (response.status === 500) {
      console.error("Internal server error");
      return {
        rStatus: "error",
        rMessage: i18n.t("Internal server error"),
      };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        console.warn("Request aborted");
        return { rStatus: "error", rMessage: "request_aborted" };
      }

      if (!retry) {
        return fetchAgentModels({ signal, retry: true });
      }
      console.error("Network request failed");
      return {
        rStatus: "error",
        rMessage: i18n.t("Network request failed. Please try again"),
      };
    }

    console.error("Unexpected response status", response.status);
    return {
      rStatus: "error",
      rMessage: i18n.t("Failed to fetch agents"),
    };
  } catch (error) {
    console.error("Error fetching agents", error);
    return {
      rStatus: "error",
      rMessage: i18n.t("Error fetching agents"),
    };
  }
}

// #endregion fetchAgentModels
